"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AdminOrgKycListQuery,
  AdminOverrideVerifyLevelRequest,
  ChangeRejectCode,
  KycRejectRequest,
} from "@chotca/config";
import { getSdk } from "../sdk";
import { qk } from "../query-keys";

export function useAdminWorkerKycList(
  query: { page?: number; limit?: number } = {},
) {
  return useQuery({
    queryKey: [...qk.admin.workerKyc, query] as const,
    queryFn: () => getSdk().admin.listWorkerKyc(query),
    staleTime: 15_000,
    placeholderData: (prev) => prev,
  });
}

export function useAdminWorkerKyc(id: string | null | undefined) {
  return useQuery({
    queryKey: qk.admin.workerKycDetail(id ?? ""),
    queryFn: () => getSdk().admin.getWorkerKyc(id!),
    enabled: !!id,
    refetchOnWindowFocus: true,
    staleTime: 60_000,
  });
}

export function useApproveWorkerKyc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => getSdk().admin.approveWorkerKyc(id),
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: qk.admin.workerKyc });
      qc.invalidateQueries({ queryKey: qk.admin.workerKycDetail(id) });
      qc.invalidateQueries({ queryKey: qk.admin.dashboard });
    },
  });
}

export function useRejectWorkerKyc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      getSdk().admin.rejectWorkerKyc(id, { reason } as KycRejectRequest),
    onSuccess: (_d, { id }) => {
      qc.invalidateQueries({ queryKey: qk.admin.workerKyc });
      qc.invalidateQueries({ queryKey: qk.admin.workerKycDetail(id) });
    },
  });
}

export function useAdminOrgKycList(query: AdminOrgKycListQuery = {}) {
  return useQuery({
    queryKey: [...qk.admin.orgKyc, query] as const,
    queryFn: () => getSdk().admin.listOrgKyc(query),
    staleTime: 15_000,
    placeholderData: (prev) => prev,
  });
}

export function useAdminOrgKyc(id: string | null | undefined) {
  return useQuery({
    queryKey: qk.admin.orgKycDetail(id ?? ""),
    queryFn: () => getSdk().admin.getOrgKycAdmin(id!),
    enabled: !!id,
    refetchOnWindowFocus: true,
    staleTime: 60_000,
  });
}

export function useApproveOrgKyc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => getSdk().admin.approveOrgKyc(id),
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: qk.admin.orgKyc });
      qc.invalidateQueries({ queryKey: qk.admin.orgKycDetail(id) });
      qc.invalidateQueries({ queryKey: qk.admin.dashboard });
    },
  });
}

export function useRejectOrgKyc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      reason,
      rejectCode,
    }: {
      id: string;
      reason: string;
      rejectCode?: ChangeRejectCode;
    }) =>
      getSdk().admin.rejectOrgKyc(id, {
        reason,
        rejectCode,
      } as KycRejectRequest),
    onSuccess: (_d, { id }) => {
      qc.invalidateQueries({ queryKey: qk.admin.orgKyc });
      qc.invalidateQueries({ queryKey: qk.admin.orgKycDetail(id) });
    },
  });
}

export function useOverrideOrgVerifyLevel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      orgId,
      body,
    }: {
      orgId: string;
      body: AdminOverrideVerifyLevelRequest;
    }) => getSdk().admin.overrideOrgVerifyLevel(orgId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.admin.orgs() });
    },
  });
}
